import { MoreOutlined, UserOutlined } from "@ant-design/icons";
// eslint-disable-next-line no-restricted-imports
import { Avatar, Button, Dropdown, Menu, Popconfirm, Space, Tabs, Typography } from "antd";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  commitLocalUpdate,
  ConnectionHandler,
  graphql,
  usePaginationFragment,
  usePreloadedQuery,
  useRelayEnvironment,
} from "react-relay/hooks";
import { Link } from "react-router-dom";

import * as CLOUD_API from "@skydio/api_util/src/backends/cloud_api";
import { RawTable, Tag, toast } from "@skydio/rivet-ui";

import LoginOverridesFilter from "components/common/filters/LoginOverridesFilter";
import LoginTypeFilter from "components/common/filters/LoginTypeFilter";
import MultiRoleFilter from "components/common/filters/MultiRoleFilterNew";
import PilotFilter from "components/common/filters/PilotFilterRivet";
import ResourceGroupFilter from "components/common/filters/ResourceGroupFilterNew";
import { legacyToRivetPagination, useRelayPagination } from "components/common/Pagination";
import { SuspenseFallbackSpin } from "components/common/SuspenseFallbackSpin";
import { useDispatch, useSelector } from "hooks/redux";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useIsAdmin, useIsUserSkydioInOrg } from "hooks/useIsAdmin";
import { useQueryLoaderWithManualFetchToAvoidSuspense } from "hooks/useQueryLoaderWithManualFetchToAvoidSuspense";
import { getOrgUuid, isUserAtLeastModerator } from "state/current_user/selectors";
import { getVariablesFromUrl, updateUrlParams } from "state/url_params/utils";
import { formatOrgPrefixedPath, formatUserName } from "utils/common";
import { getReadableOrgRoleFromName, getResourceGroupDisplayName } from "utils/team";
import { trackUserAction } from "utils/trackEvent";

import { SettingsPageViewBigHeader } from "../settings/SettingsPageView";
import EnableOrganizationMembers from "./EnableMember";
import InviteMember from "./InviteMember";
import OrgLoginMethods from "./OrgLoginMethods";
import UserProfile from "./UserProfile";

import styles from "./TeamView.module.scss";

import type { FC, JSX } from "react";
import type { PreloadedQuery } from "react-relay/hooks";
import type {
  TeamView_organization$data,
  TeamView_organization$key,
} from "__generated__/TeamView_organization.graphql";
import type { TeamViewQuery } from "__generated__/TeamViewQuery.graphql";
import type { ConnectionNodeType } from "components/common/Pagination";
import type { SettingsPageViewProps } from "../settings/SettingsPageView";

const userFragment = graphql`
  fragment TeamView_organization on Organization
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 10 }
    cursor: { type: "String" }
    uuid: { type: "UUID" }
    role: { type: "String" }
    resourceGroupIds: { type: "[String]" }
    resourceGroupPermissions: { type: "[String]" }
    loginTypes: { type: "[String]" }
    loginOverriden: { type: "Boolean" }
  )
  @refetchable(queryName: "TeamRefetchQuery") {
    id
    users(
      uuid: $uuid
      role: $role
      resourceGroupIds: $resourceGroupIds
      resourceGroupPermissions: $resourceGroupPermissions
      loginTypes: $loginTypes
      loginOverriden: $loginOverriden
      first: $count
      after: $cursor
    ) @connection(key: "TeamView_users") {
      __id
      totalCount
      edges {
        node {
          id
          email
          uuid
          displayName
          firstName
          lastName
          phoneNumber
          expiry
          organizationPermission
          supportedLoginOptions {
            loginType
            identityProviderConfiguration {
              name
              providerType
            }
          }
          loginTypes {
            edges {
              node {
                loginType
                authEnabled
                identityProviderConfiguration {
                  id
                  name
                  providerType
                  uuid
                }
              }
            }
          }
          ...UserProfile_loginTypes
          loginTypesOverriden
          resourceGroupLinks {
            edges {
              node {
                id
                resourceGroupId
                resourceGroupName
                resourceGroupPermission
              }
            }
          }
        }
      }
    }
  }
`;

export const identityProviderFragment = graphql`
  fragment TeamView_identityProviderFragment on Organization {
    identityProviderConfigurations {
      edges {
        node {
          id
          uuid
          name
          providerType
        }
      }
    }
  }
`;

const query = graphql`
  query TeamViewQuery(
    $count: Int!
    $cursor: String
    $orgUuid: String!
    $userUuid: UUID
    $role: String
    $resourceGroupIds: [String]
    $resourceGroupPermissions: [String]
    $loginTypes: [String]
    $loginOverriden: Boolean
  ) {
    organization(uuid: $orgUuid) {
      id
      ...TeamView_organization
        @arguments(
          count: $count
          cursor: $cursor
          uuid: $userUuid
          role: $role
          resourceGroupIds: $resourceGroupIds
          resourceGroupPermissions: $resourceGroupPermissions
          loginTypes: $loginTypes
          loginOverriden: $loginOverriden
        )
      dataLevel
      name
      ...TeamView_identityProviderFragment
      ...SsoProviderLoginTypeSelection_idpData
      ...LoginTypeFilter_orgIdps
      ...OrgLoginMethods_loginMethods
    }
  }
`;

type User = ConnectionNodeType<TeamView_organization$data["users"]>;

interface Props extends SettingsPageViewProps {
  queryReference: PreloadedQuery<TeamViewQuery>;
  nullGroupFilter: boolean;
}

const pageSize = 25;

interface UserActionsDropdownProps {
  user: User | null;
  onEdit: () => void;
  onRemove: () => void;
  readOnly?: boolean;
}

const UserActionsDropdown: FC<UserActionsDropdownProps> = ({
  user,
  onEdit,
  onRemove,
  readOnly,
}) => {
  const handleRemove = useCallback(() => {
    onRemove();
  }, [onRemove]);
  const handleEdit = useCallback(() => {
    onEdit();
  }, [onEdit]);
  const userEmail = user?.email;
  const userActionsMenu = useMemo(
    () => (
      <Menu>
        <Menu.Item key="edit" disabled={!userEmail || (readOnly ?? false)} onClick={handleEdit}>
          <Typography.Text disabled={readOnly}>Edit</Typography.Text>
        </Menu.Item>
        <Popconfirm
          title="Are you sure you want to delete this user?"
          onConfirm={handleRemove}
          okText="Yes"
          cancelText="No"
          placement="right"
        >
          <Menu.Item disabled={readOnly} key="delete">
            <Typography.Text disabled={readOnly} type="danger">
              Remove User
            </Typography.Text>
          </Menu.Item>
        </Popconfirm>
      </Menu>
    ),
    [userEmail, readOnly, handleEdit, handleRemove]
  );
  const moreOutlined = useMemo(() => <MoreOutlined />, []);
  const dropdownTrigger = useMemo(() => ["click"] as ("click" | "hover" | "contextMenu")[], []);
  if (user === null || user === undefined) {
    return null;
  }

  return (
    <Dropdown overlay={userActionsMenu} trigger={dropdownTrigger} placement="bottomRight">
      <Button type="text" icon={moreOutlined} />
    </Dropdown>
  );
};

export const getUserRoles = (
  user: Partial<User> | null,
  orgName: string,
  orgUuid: string,
  supportUserExpiryEnabled: boolean
): React.JSX.Element | JSX.Element[] | null => {
  if (user === null || user === undefined) {
    return null;
  }
  const resourceGroupLinks = user.resourceGroupLinks?.edges ?? [];
  // user is a pilot if they have no resource group links (i.e not cloud user or remote pilot) and is not an org admin
  if (user.organizationPermission === "MEMBER" && resourceGroupLinks.length === 0) {
    return <div>{getReadableOrgRoleFromName(user.organizationPermission ?? "")}</div>;
  }

  const userRoles = new Array<JSX.Element>();
  // user is an organization admin if they have admin permission on org
  if (user.organizationPermission === "ADMIN") {
    userRoles.push(<div>Organization Admin</div>);
  }
  if (user.organizationPermission === "SKYDIO_SUPERUSER") {
    if (supportUserExpiryEnabled) {
      userRoles.push(<div>Skydio Superuser</div>);
    } else {
      userRoles.push(<div>Organization Admin</div>);
    }
  }
  if (user.organizationPermission === "SKYDIO_READONLY") {
    if (supportUserExpiryEnabled) {
      userRoles.push(<div>Skydio Read-Only</div>);
    } else {
      userRoles.push(<div>Cloud User</div>);
    }
  }

  for (const rg of resourceGroupLinks) {
    const readableRoleName = getReadableOrgRoleFromName(rg?.node?.resourceGroupPermission ?? "");
    const displayRoleName = readableRoleName === "User" ? "Cloud User" : readableRoleName;
    userRoles.push(
      <div>
        {displayRoleName} in{" "}
        <Link
          to={formatOrgPrefixedPath(
            orgUuid,
            `/settings/resource-groups/${rg!.node?.resourceGroupId}`
          )}
        >
          {getResourceGroupDisplayName(orgName, rg?.node?.resourceGroupName ?? "")}
        </Link>{" "}
      </div>
    );
  }
  return userRoles;
};

const TeamView = ({ queryReference, nullGroupFilter, ...props }: Props) => {
  const dispatch = useDispatch();
  const environment = useRelayEnvironment();

  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState("users");

  const queryData = usePreloadedQuery<TeamViewQuery>(query, queryReference);

  const orgUuid = useSelector(getOrgUuid);
  const orgId = queryData?.organization?.id ?? "";

  const nameCellStyle = useMemo(() => ({ display: "flex", alignItems: "center", gap: "8px" }), []);
  const userNameStyle = useMemo(() => ({ paddingLeft: "8px" }), []);

  const atLeastModerator = useSelector(isUserAtLeastModerator);
  const isAdmin = useIsAdmin();
  const isSkydioInOrg = useIsUserSkydioInOrg();
  const isInternalOrg = queryData?.organization?.dataLevel === "INTERNAL";
  const orgName = queryData?.organization?.name ?? "No Organization";
  const supportUserExpiryEnabled = useFeatureFlag("supportUserExpiry");
  const enableDistributionList = useFeatureFlag("enableDistributionList");

  const showFullUI = isAdmin || isSkydioInOrg;

  const showGranularPermissionsAccessAndActions = showFullUI;

  const showUserExpiry = useMemo(
    () => showFullUI && supportUserExpiryEnabled,
    [showFullUI, supportUserExpiryEnabled]
  );

  const userOutlinedIcon = useMemo(() => <UserOutlined />, []);

  const paginationQuery = usePaginationFragment<TeamViewQuery, TeamView_organization$key>(
    userFragment,
    queryData?.organization ?? null
  );

  const [paginationProps, data, resetPage] = useRelayPagination(
    paginationQuery,
    pageSize,
    paginationQuery.data?.users
  );

  const reloadUsers = useCallback(() => {
    paginationQuery.refetch({}, { fetchPolicy: "network-only" });
  }, [paginationQuery]);

  const { t } = useTranslation("user");

  const handleShowProfile = useCallback(
    (user: User | null) => () => {
      setProfileUser(user);
      trackUserAction("open_user_profile", { user_email: user?.email });
    },
    [setProfileUser]
  );

  const renderUserAccess = useCallback(
    (user: User | null) => getUserRoles(user, orgName, orgUuid, supportUserExpiryEnabled),
    [orgName, orgUuid, supportUserExpiryEnabled]
  );

  const renderUserLoginTypes = (user: User | null) => {
    if (user === null || user === undefined) {
      return <></>;
    }

    const transformedTypes: string[] = [];
    for (const loginType of user.supportedLoginOptions ?? []) {
      if (loginType === null) {
        continue;
      }
      switch (loginType.loginType) {
        case "ONE_TIME_PASSWORD":
          transformedTypes.push("Email");
          break;
        case "SSO":
          transformedTypes.push(
            `SSO (${loginType.identityProviderConfiguration?.name ?? "Unknown Identity Provider"})`
          );
          break;
        case "AXON":
          transformedTypes.push("Axon");
          break;
      }
    }
    return (
      <div className={"space-x-1"}>
        {transformedTypes.map(loginType => (
          <Tag variant="solid">{loginType}</Tag>
        ))}
      </div>
    );
  };

  const renderUserExpiry = (user: User | null) => {
    if (!user) {
      return null;
    }

    if (!user.expiry) {
      return <Typography.Text>Never</Typography.Text>;
    }

    return <Typography.Text>{moment(user.expiry).format("YYYY-MM-DD HH:mm:ssZ")}</Typography.Text>;
  };

  // Manually remove user record from relay store after removing from org in order to update table.
  // As we do not use a mutation for deleting users, we can't take advantage of the @deleteEdge directive, which would eliminate the need for this.
  const deleteUserRecord = useCallback(
    (userId: string, organizationId: string) => {
      commitLocalUpdate(environment, store => {
        const organizationRecord = store.get(organizationId);

        if (organizationRecord) {
          const connectionId = paginationQuery.data?.users?.__id;
          if (!connectionId) {
            return;
          }
          const connection = store.get(connectionId);
          if (connection) {
            ConnectionHandler.deleteNode(connection, userId);
          }
        }
      });
    },
    [environment, paginationQuery.data?.users?.__id]
  );

  const handleRemoveAccount = useCallback(
    (email: string, userId: string) => async () => {
      try {
        // TODO: This should be moved to graphql mutation
        await dispatch(
          CLOUD_API.modules.organizations.asyncThunks.removeUserFromOrganization({
            email,
            uuid: orgUuid,
          })
        );

        deleteUserRecord(userId, orgId);

        toast.success(t("messages.removeSuccess", { email }));
        trackUserAction("remove_user_from_org", { user_email: email });
      } catch (error) {
        // Handle error if needed
        console.error("Error removing user from organization:", error);
        toast.error(t("messages.removeError", { email }));
      }
    },
    [orgId, orgUuid, dispatch, t, deleteUserRecord]
  );

  const userInvitesDeprecated = useFeatureFlag("deprecateOrgInvitations");

  const userConnection = useMemo(
    () => paginationQuery.data?.users?.__id,
    [paginationQuery.data?.users]
  );

  const extra = () => {
    if (showFullUI || isInternalOrg) {
      if (userInvitesDeprecated) {
        return <EnableOrganizationMembers connection={userConnection} />;
      } else {
        return <InviteMember />;
      }
    }
  };

  const tabClickCallback = useCallback(
    (key: string) => {
      setActiveTab(key);
      dispatch(
        // Clear filters when switching tabs
        updateUrlParams({
          userId: undefined,
          role: undefined,
          resourceGroupIds: undefined,
          resourceGroupRoles: undefined,
          loginTypes: undefined,
          loginOverriden: undefined,
        })
      );
    },
    [dispatch]
  );

  const handleCancelUserProfile = useCallback(() => {
    setProfileUser(null);
  }, [setProfileUser]);

  return (
    <SettingsPageViewBigHeader
      {...props}
      title="Users"
      subTitle="Manage the settings related to users and login flows."
      extra={extra()}
    >
      <SuspenseFallbackSpin>
        <Tabs size="large" activeKey={activeTab} onTabClick={tabClickCallback}>
          <Tabs.TabPane key="users" tab="Users">
            <Space direction="vertical" className="w-full">
              <div className="flex flex-row justify-between">
                <Space>
                  <PilotFilter
                    label={"Users"}
                    placeholder="Search Users"
                    paramKey="userId"
                    onSelect={resetPage}
                  />
                  {showFullUI ? (
                    <>
                      <MultiRoleFilter
                        paramKey="resourceGroupRoles"
                        label="Role"
                        placeholder="Role"
                        onSelect={resetPage}
                        includeRemotePilot={true}
                      />
                      <ResourceGroupFilter
                        paramKey="resourceGroupIds"
                        label="Group"
                        placeholder="Group"
                        onSelect={resetPage}
                        disabled={nullGroupFilter}
                      />
                    </>
                  ) : null}
                  {isAdmin && queryData.organization && (
                    <>
                      <LoginTypeFilter
                        paramKey="loginTypes"
                        onSelect={resetPage}
                        idpDataRef={queryData.organization}
                      />
                      <div className="flex items-center gap-2">
                        <LoginOverridesFilter paramKey="loginOverriden" onSelect={resetPage} />
                        <a
                          href="https://support.skydio.com/hc/en-us/articles/4409349509275-How-to-add-users-to-Skydio-Cloud"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700"
                        >
                          Learn more
                        </a>
                      </div>
                    </>
                  )}
                </Space>
              </div>
              <RawTable>
                <RawTable.Header>
                  <RawTable.Row>
                    <RawTable.Head>User</RawTable.Head>
                    {showGranularPermissionsAccessAndActions && (
                      <RawTable.Head>Access</RawTable.Head>
                    )}
                    {isAdmin && <RawTable.Head>Login Methods</RawTable.Head>}
                    {showUserExpiry && <RawTable.Head>Expiration</RawTable.Head>}
                    {/* Show empty column for actions dropdown */}
                    {showGranularPermissionsAccessAndActions && <RawTable.Head />}
                  </RawTable.Row>
                </RawTable.Header>
                <RawTable.Body>
                  {data.length > 0 ? (
                    data.map(user => {
                      const userName = formatUserName(user.node);
                      return (
                        <RawTable.Row id={user.node?.id}>
                          {/* Name column */}
                          <RawTable.Cell>
                            <div style={nameCellStyle}>
                              <Avatar icon={userOutlinedIcon} />
                              <div>
                                {atLeastModerator && user.node?.email && (
                                  <>
                                    <Typography.Text className={styles.name} style={userNameStyle}>
                                      {userName}
                                    </Typography.Text>
                                    <Typography.Text
                                      style={userNameStyle}
                                      type="secondary"
                                      className="block text-xs"
                                    >
                                      {userName !== user.node?.email ? user.node?.email : ""}
                                      {userName !== user.node?.email &&
                                        user.node?.email &&
                                        enableDistributionList &&
                                        user.node?.phoneNumber &&
                                        " | "}
                                      {enableDistributionList && user.node?.phoneNumber
                                        ? parsePhoneNumberWithError(
                                            user.node?.phoneNumber
                                          ).formatInternational()
                                        : ""}
                                    </Typography.Text>
                                  </>
                                )}
                              </div>
                            </div>
                          </RawTable.Cell>

                          {/* Granular permissions access */}
                          {showGranularPermissionsAccessAndActions && (
                            <RawTable.Cell>{renderUserAccess(user.node)}</RawTable.Cell>
                          )}

                          {/* Login methods column */}
                          {isAdmin && (
                            <RawTable.Cell>{renderUserLoginTypes(user.node)}</RawTable.Cell>
                          )}
                          {showUserExpiry && (
                            <RawTable.Cell>{renderUserExpiry(user.node)}</RawTable.Cell>
                          )}

                          {/* Granular permissions actions */}
                          {showGranularPermissionsAccessAndActions && (
                            <RawTable.Cell>
                              <UserActionsDropdown
                                user={user.node}
                                onEdit={handleShowProfile(user.node)}
                                onRemove={handleRemoveAccount(
                                  user.node?.email ?? "",
                                  user.node?.id ?? ""
                                )}
                                readOnly={!isAdmin}
                              />
                            </RawTable.Cell>
                          )}
                        </RawTable.Row>
                      );
                    })
                  ) : (
                    <RawTable.Row>
                      <RawTable.Cell className={styles.empty} colSpan={24}>
                        <div>No users match your search</div>
                        <div>
                          <Link to={formatOrgPrefixedPath(orgUuid, "/settings/team")}>
                            Reset filters
                          </Link>
                        </div>
                      </RawTable.Cell>
                    </RawTable.Row>
                  )}
                </RawTable.Body>
                <RawTable.Pagination {...legacyToRivetPagination(paginationProps, resetPage)} />
              </RawTable>
            </Space>
            {queryData.organization && profileUser && (
              <UserProfile
                onCancel={handleCancelUserProfile}
                onRemove={handleRemoveAccount(profileUser?.email ?? "", profileUser?.id ?? "")}
                idpDataRef={queryData.organization}
                userRef={profileUser}
                statsRef={queryData.organization}
              />
            )}
          </Tabs.TabPane>
          {isAdmin && (
            <Tabs.TabPane key="loginMethods" tab="Login Methods">
              {queryData.organization && (
                <OrgLoginMethods loginMethodsRef={queryData.organization} onUpdate={reloadUsers} />
              )}
            </Tabs.TabPane>
          )}
        </Tabs>
      </SuspenseFallbackSpin>
    </SettingsPageViewBigHeader>
  );
};

const TeamViewWrapper: FC<SettingsPageViewProps> = props => {
  const orgUuid = useSelector(getOrgUuid);

  const [queryReference, fetch] =
    useQueryLoaderWithManualFetchToAvoidSuspense<TeamViewQuery>(query);

  const { userId, role, resourceGroupIds, resourceGroupRoles, loginTypes, loginOverriden } =
    useSelector(getVariablesFromUrl);
  const resourceGroupPermissionsArray = resourceGroupRoles
    ? resourceGroupRoles.split(",")
    : undefined;
  const nullGroupFilter = Boolean(
    resourceGroupPermissionsArray &&
      (resourceGroupPermissionsArray.includes("ADMIN") ||
        resourceGroupPermissionsArray.includes("MEMBER"))
  );
  const resourceGroupIdsArray =
    !nullGroupFilter && resourceGroupIds && resourceGroupIds !== ""
      ? resourceGroupIds.split(",")
      : undefined;

  const loginTypesArray = loginTypes ? loginTypes.split(",") : undefined;

  useEffect(() => {
    fetch({
      orgUuid,
      count: pageSize,
      userUuid: userId,
      role,
      resourceGroupIds: resourceGroupIdsArray,
      resourceGroupPermissions: resourceGroupPermissionsArray,
      loginTypes: loginTypesArray,
      loginOverriden: loginOverriden !== undefined ? loginOverriden === "true" : undefined,
    });
  }, [
    fetch,
    orgUuid,
    userId,
    role,
    resourceGroupIds,
    resourceGroupRoles,
    resourceGroupIdsArray,
    resourceGroupPermissionsArray,
    loginTypesArray,
    loginOverriden,
  ]);

  return queryReference ? (
    <TeamView queryReference={queryReference} nullGroupFilter={nullGroupFilter} {...props} />
  ) : null;
};

export default TeamViewWrapper;
